import { EmptyState } from '@/components/common/empty-state'
import { SkeletonItem } from '@/components/common/skeleton-item'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreateApiKeyButton } from '@/features/apiKeys/components/CreateApiKeyButton'
import { DeleteApiKeyDialog } from '@/features/apiKeys/components/DeleteApiKeyDialog'
import { useOrgApiKeys } from '@/features/apiKeys/hooks/useApiKeys'
import { Key, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

export const ApiKeyList = () => {
  const { data: apiKeys, isLoading, error, isError } = useOrgApiKeys()
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error('Failed to load API keys', {
        description:
          error instanceof Error
            ? error.message
            : 'Please try again or contact support',
      })
    }
  }, [isLoading, isError, error])

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-8 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">API Keys</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create and manage your API keys
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <CreateApiKeyButton />
        </div>
      </div>

      <Card>
        <CardHeader className="border-b">
          <CardTitle>Your API Keys</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {isLoading ? (
            <SkeletonItem />
          ) : apiKeys?.length === 0 ? (
            <EmptyState
              icon={Key}
              title="No API keys"
              description="Get started by creating a new API key."
            />
          ) : (
            <div className="space-y-4">
              {apiKeys?.map((key) => (
                <div
                  key={key.id}
                  className="flex justify-between items-center p-4 rounded-lg transition-colors duration-150 bg-muted hover:bg-muted/80"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{key.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {key.key_preview}
                    </p>
                    {key.last_used_at && (
                      <p className="text-sm text-muted-foreground">
                        Last used:{' '}
                        {new Date(key.last_used_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    aria-label="Delete API Key"
                    className="inline-flex items-center px-3 py-1 text-sm font-medium leading-4 rounded-md border border-transparent"
                    onClick={() => setDeleteKeyId(key.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <DeleteApiKeyDialog
        keyId={deleteKeyId}
        onClose={() => setDeleteKeyId(null)}
      />
    </div>
  )
}
